/**
 * Order detail panel — the drawer behind the eye icon on an orders table.
 *
 * Shared by the global Orders Management page and a brand's own Orders tab, because those two
 * tables list the same rows from the same endpoint family and an admin moving between them
 * should not find the same order described two different ways. It was inline in the global
 * page first; a brand tab needed it too, and a second copy is how the two drift.
 *
 * WHAT IT ASSUMES OF `order`: nothing. Every field is optional and every absent one renders as
 * a dash or as "Not a retailer order", because a brand selling direct has no Mirakl id, no
 * retailer status and no tracking push — and those are facts about the order, not gaps in it.
 * The Shopify link needs `brand.shopDomain`, and the footer simply does not render without it,
 * so a caller whose rows do not carry the brand is not broken by its absence.
 *
 * Polaris marks `Sheet` deprecated in 13.9.5. It is used anyway because it is exactly this
 * pattern and the console is pinned to 13.9.5 — a hand-rolled panel would duplicate a
 * component Polaris still ships. Revisit on a Polaris major upgrade.
 */

import {
  Sheet, Box, Button, Text, Badge, BlockStack, InlineStack, Tooltip,
} from '@shopify/polaris';
import apiClient from '../utils/api';

function drawerSections(order) {
  const money = (v) => apiClient.formatCurrency(v || 0);

  // ONE reading of who shipped it, for both the row below and the shipping fee further
  // down. Keying those two off different fields - the label for one, the raw enum for the
  // other - lets a caller that sends only one of them print a dash beside a fee that says
  // "brand shipped". They arrive together from the orders endpoint today; that is a fact
  // about one caller, not a property of this component.
  const brandShipped = order.shippedBy === 'BRAND';
  const dt = (v) => (v ? new Date(v).toLocaleString() : null);
  const notRetailer = 'Not a retailer order';

  const trackingNode = (() => {
    if (!order.isRetailerOrder) return null;
    if (order.shipValidatedAt) return <Badge tone="success">Sent and confirmed</Badge>;
    if (order.trackingPushedAt) {
      return (
        <Tooltip content="The retailer has the tracking number but has not confirmed the shipment. On pay-on-shipment terms that means unpaid.">
          <Badge tone="warning">Sent, not confirmed</Badge>
        </Tooltip>
      );
    }
    if (order.shippedAt) return <Badge tone="critical">Not sent</Badge>;
    return <Badge>Not shipped yet</Badge>;
  })();

  return [
    {
      title: 'Identity',
      rows: [
        { label: 'Shopify order', value: order.orderNumber },
        {
          label: 'Mirakl order ID',
          value: order.miraklOrderId ?? notRetailer,
          tone: order.miraklOrderId ? undefined : 'subdued',
        },
        { label: 'Source', value: order.appName || order.marketplace || '—' },
        { label: 'Retailer', value: order.marketplace || '—' },
      ],
    },
    {
      title: 'Status',
      rows: [
        { label: 'Billing status', field: 'financialStatus', value: order.financialStatus || '—' },
        {
          label: 'Retailer status',
          field: 'miraklStatus',
          value: order.miraklStatus ?? notRetailer,
          tone: order.miraklStatus ? undefined : 'subdued',
        },
        {
          label: 'Connector stage',
          value: order.connectorStage ?? notRetailer,
          tone: order.connectorStage ? undefined : 'subdued',
        },
      ],
    },
    {
      title: 'Fulfillment & tracking',
      rows: [
        { label: 'Fulfillment', value: order.fulfillmentStatus || 'Unfulfilled' },
        {
          label: 'Shipped by',
          // The server computes the label because it holds the brand name. Falling back to
          // the raw value keeps this row and the shipping fee below telling one story.
          value: order.shippedByLabel || (brandShipped ? 'The brand' : null),
          tone: order.shippedByLabel ? undefined : 'subdued',
        },
        // Console only. The brand-facing app never sees a warehouse name, so this is the
        // one place 2W staff can check WHICH location an order left from when a brand
        // queries why its shipping fee is what it is.
        { label: 'Fulfilled from', value: order.fulfilledFrom || '—', tone: 'subdued' },
        { label: 'Tracking number', value: order.trackingNumber ?? '—' },
        { label: 'Carrier', value: order.carrier || order.shippingMethod || '—' },
        { label: 'Shipped at', value: dt(order.shippedAt) ?? '—' },
        {
          label: 'Tracking sent to retailer',
          node: trackingNode,
          value: notRetailer,
          tone: 'subdued',
        },
      ],
    },
    {
      title: 'Fees & payout',
      rows: [
        { label: 'Gross sales', value: money(order.grossAmount) },
        {
          label: 'Refund',
          value: order.refundAmount ? `-${money(Math.abs(order.refundAmount))}` : money(0),
          tone: order.refundAmount ? 'critical' : undefined,
        },
        { label: 'Net sales', value: money(order.netAmount) },
        {
          label: 'Marketplace commission',
          value: money(order.appliedMarketplaceCommissionAmount),
        },
        { label: '2W commission', value: money(order.applied2wCommissionAmount) },
        {
          label: 'Shipping fee',
          // A brand-shipped order owes nothing, which is not the same as an order nobody
          // has priced yet - and both read $0.00. Said here rather than left to the
          // Shipped by row above, because this is the figure someone is querying.
          value: brandShipped
            ? `${money(0)} — brand shipped`
            : money(order.appliedShippingFee),
        },
        // WHY THAT FIGURE, when it is a figure nobody finished working out. Blank for the
        // three statuses that are answers and for an order priced before the reason was
        // recorded, so this row appears only where there is something to settle - which is
        // what keeps it a signal rather than decoration on every ordinary order.
        ...(order.shippingFeeStatusLabel
          ? [{
              label: 'Needs review',
              value: order.shippingFeeStatusLabel,
              tone: 'caution',
            }]
          : []),
        // The order is held at $0 until the month is reported, so the figure above and the
        // invoice disagree for as long as nobody acts. Said here rather than discovered.
        ...(order.shippingFeePendingFallback > 0
          ? [{
              label: 'Fallback if unresolved',
              value: money(order.shippingFeePendingFallback),
              tone: 'caution',
            }]
          : []),
        { label: 'Return fee', value: money(order.appliedReturnFee) },
        {
          label: 'Net payout',
          // Derived, not stored on the row — the same arithmetic the table's Net Payout
          // column uses, so the two can never disagree.
          value: money(
            (order.netAmount || 0)
            - (order.appliedMarketplaceCommissionAmount || 0)
            - (order.applied2wCommissionAmount || 0)
            - (order.appliedShippingFee || 0)
            - (order.appliedReturnFee || 0)
          ),
          strong: true,
        },
      ],
    },
    {
      title: 'Weight',
      rows: [
        {
          label: 'Shipment weight',
          value: order.totalWeight ? `${Number(order.totalWeight).toFixed(2)} lb` : '—',
        },
      ],
    },
  ];
}

export default function OrderDetailPanel({ order, open, onClose }) {
  if (!order) return null;

  return (
    <Sheet open={open} onClose={onClose} accessibilityLabel="Order detail">
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Sticky header — the order number stays visible while the body scrolls. */}
        <Box padding="400" borderBlockEndWidth="025" borderColor="border" background="bg-surface">
          <InlineStack align="space-between" blockAlign="center" wrap={false}>
            <BlockStack gap="050">
              <Text variant="headingMd" as="h2">Order {order.orderNumber}</Text>
              <Text variant="bodySm" tone="subdued">{order.brand?.name ?? order.marketplace ?? '—'}</Text>
            </BlockStack>
            <Button variant="tertiary" onClick={onClose} accessibilityLabel="Close order detail">
              Close
            </Button>
          </InlineStack>
        </Box>

        <Box padding="400" overflowY="scroll">
          <BlockStack gap="500">
            {/* Identity first because the admin arrived here from a row and needs to confirm
                which order this is; payout last because it is the conclusion, not the lookup. */}
            {drawerSections(order).map((section) => (
              <BlockStack gap="200" key={section.title}>
                <Text variant="headingSm" as="h3" tone="subdued">{section.title}</Text>
                <BlockStack gap="0">
                  {section.rows.map((row) => (
                    <Box
                      key={row.label}
                      paddingBlock="200"
                      borderBlockEndWidth="025"
                      borderColor="border-secondary"
                    >
                      <InlineStack align="space-between" blockAlign="start" gap="400" wrap={false}>
                        <InlineStack gap="150" blockAlign="center">
                          <Text variant="bodySm" tone="subdued">{row.label}</Text>
                          {row.field && (
                            <Text variant="bodySm" tone="subdued">
                              <code style={{ fontSize: 10.5 }}>{row.field}</code>
                            </Text>
                          )}
                        </InlineStack>
                        <Box>
                          {row.node ?? (
                            <Text
                              variant="bodySm"
                              alignment="end"
                              fontWeight={row.strong ? 'bold' : 'semibold'}
                              tone={row.tone}
                            >
                              {row.value ?? '\u2014'}
                            </Text>
                          )}
                        </Box>
                      </InlineStack>
                    </Box>
                  ))}
                </BlockStack>
              </BlockStack>
            ))}
          </BlockStack>
        </Box>

        {/* Footer pinned below the scroll area so the primary action is always reachable,
            however long the order detail runs. */}
        {order.brand?.shopDomain && order.shopifyOrderId && (
          <Box padding="400" borderBlockStartWidth="025" borderColor="border" background="bg-surface">
            <Button
              variant="primary"
              fullWidth
              url={`https://${order.brand.shopDomain}/admin/orders/${order.shopifyOrderId}`}
              target="_blank"
            >
              View in Shopify
            </Button>
          </Box>
        )}
      </div>
    </Sheet>
  );
}
